import React, { Component } from 'react';
import 'components/transactionEditor/Wizards/BzmTreeTable.css';
import clsx from 'clsx';
import { RequestTabState } from '../../../views/AssetCatalog/Transactions/TransactionEditor/RequestTab/RequestTab';
import i18n from '../../../i18n';
import BzmTreeTable from './BzmTreeTable';

type ResponseDetailedPanelProps = {
  requestBodyTreeJson?: string;
  requestTabState: RequestTabState;
  updateNodeValue: (id: string, value: string) => void;
  id: string;
  requestBodyType: string;
};
class DynamicValuesPanel extends Component<ResponseDetailedPanelProps> {
  state = {
    selectedRow: -9,
  };

  addQueryParams = (data: any) => {
    if (this.props.requestTabState.queryParams.length > 0) {
      data.push({
        name: i18n.t('transactions:editor.requestQueryParams'),
        id: -1,
        type: 'GROUP',
        parentId: 0,
      });
      this.props.requestTabState.queryParams.forEach((val: any) =>
        data.push({
          name: val.name,
          id: -1,
          type: 'LEAF',
          leafText: val.value,
          parentId: 0,
          qualifier: 'request.query.' + val.name,
        }),
      );
    }
  };

  addRequestHeaders = (data: any) => {
    if (this.props.requestTabState.headers.length > 0) {
      data.push({
        name: i18n.t('transactions:editor.requestHeaders'),
        id: -1,
        type: 'GROUP',
        parentId: 0,
      });
      this.props.requestTabState.headers.forEach((val: any) =>
        data.push({
          name: val.name,
          id: -1,
          type: 'LEAF',
          leafText: val.value,
          parentId: 0,
          qualifier: 'request.headers.' + val.name,
        }),
      );
    }
  };

  addRequestCookies = (data: any) => {
    if (this.props.requestTabState.cookies.length > 0) {
      data.push({
        name: i18n.t('transactions:editor.requestCookies'),
        id: -1,
        type: 'GROUP',
        parentId: 0,
      });
      this.props.requestTabState.cookies.forEach((val: any) =>
        data.push({
          name: val.name,
          id: -1,
          type: 'LEAF',
          leafText: val.value,
          parentId: 0,
          qualifier: 'request.cookies.' + val.name,
        }),
      );
    }
  };

  addRequestBody = (data: any) => {
    if (this.props.requestBodyTreeJson) {
      data.push({
        name: i18n.t('transactions:editor.requestBody'),
        id: -1,
        type: 'GROUP',
        parentId: 0,
      });
      const reqdata = this.props.requestBodyTreeJson ? JSON.parse(this.props.requestBodyTreeJson) : data;
      reqdata.forEach((val: any) => {
        val.qualifier = 'request.body';
        data.push(Object.assign({}, val));
      });
    }
  };

  addCommonParams = (data: any) => {
    data.push({
      name: i18n.t('transactions:editor.commonParameters'),
      id: -1,
      type: 'GROUP',
      parentId: 0,
    });
    data.push({
      name: 'request.url',
      id: -1,
      type: 'LEAF',
      leafText: this.props.requestTabState.url.matchingValue,
      parentId: 0,
      qualifier: 'request.url',
    });
    data.push({
      name: 'request.path',
      id: -1,
      type: 'LEAF',
      leafText: this.props.requestTabState.path,
      parentId: 0,
      qualifier: 'request.path',
    });
    data.push({
      name: 'request.method',
      id: -1,
      type: 'LEAF',
      leafText: this.props.requestTabState.method,
      parentId: 0,
      qualifier: 'request.method',
    });
    data.push({
      name: 'request.host',
      id: -1,
      type: 'LEAF',
      leafText: this.props.requestTabState.host,
      parentId: 0,
      qualifier: 'request.host',
    });
  };

  updateValue = (event: any, selectedRow: any) => {
    if (selectedRow.type === 'LEAF') {
      this.setState({ selectedRow: selectedRow.tableData.id });
      let selectedQualifier = '${' + selectedRow.qualifier + '}';
      if (selectedRow.qualifier === 'request.body') {
        const pathNodes = this.getPathNodes(selectedRow.tableData.id);
        const path = this.generatePaths(pathNodes, this.props.requestBodyType);
        if (path !== '') {
          selectedQualifier = this.formPathBasedDynamicValue(this.props.requestBodyType, path);
        }
      }
      this.props.updateNodeValue(this.props.id, selectedQualifier);
    } else {
      return;
    }
  };

  formPathBasedDynamicValue = (type: string, path: string) => {
    if (type === 'xml') {
      return "${xPath request.body '" + path + "'}";
    } else {
      return "${jsonPath request.body '" + path + "'}";
    }
  };

  getPathNodes = (selectedRowId: number) => {
    const pathNodes = [];
    let elementToFind = selectedRowId;
    if (this.props.requestBodyTreeJson) {
      const obj = JSON.parse(this.props.requestBodyTreeJson);
      if (elementToFind === 0) {
        pathNodes.push(
          obj.filter(function(value: any) {
            return value.id === elementToFind;
          })[0],
        );
      }

      while (elementToFind !== 0) {
        const nodeObj = obj.filter(function(value: any) {
          return value.id === elementToFind;
        })[0];
        elementToFind = nodeObj.parentId ? nodeObj.parentId : 0;
        pathNodes.push(nodeObj);
      }
    }
    return pathNodes;
  };

  generatePaths = (pathNodes: any, type: string) => {
    if (type === 'xml') {
      return this.generateXPathFromNodes(pathNodes);
    } else if (type === 'json') {
      return this.generateJsonPathFromNodes(pathNodes);
    } else {
      return '';
    }
  };

  generateXPathFromNodes = (pathNodes: any) => {
    let xpath = '';
    const connector = '/';
    for (let i = pathNodes.length - 1; i >= 0; i--) {
      xpath += connector + this.checkForNamespaceIgnore(pathNodes[i].name);
    }
    xpath += '/text()';
    return xpath;
  };

  checkForNamespaceIgnore = (nodeName: string) => {
    if (nodeName.includes(':')) {
      return nodeName.split(':')[1];
    } else {
      return nodeName;
    }
  };

  generateJsonPathFromNodes = (pathNodes: any) => {
    let jsonPath = '$';
    for (let i = pathNodes.length - 1; i > 0; i--) {
      jsonPath += this.updatePathWithArrayIndex(pathNodes[i]);
    }
    jsonPath += this.updatePathWithArrayIndex(pathNodes[0]);
    return jsonPath;
  };

  updatePathWithArrayIndex = (pathNode: any) => {
    const connector = '.';
    if (pathNode.idx !== -1) {
      return '[' + pathNode.idx + ']' + connector + pathNode.name;
    } else {
      return connector + pathNode.name;
    }
  };

  render() {
    const data: any = [];

    this.addRequestBody(data);
    this.addQueryParams(data);
    this.addRequestHeaders(data);
    this.addRequestCookies(data);
    //transaction type MESSAGING does not use url, path, etc... so do not add condition
    this.props.requestTabState.url !== null && this.addCommonParams(data);

    return (
      <BzmTreeTable
        data={data}
        selectedRow={this.state.selectedRow}
        parentChildData={(row, rows) => rows.find((a: any) => a.id === row.parentId)}
        columns={[
          {
            title: i18n.t('transactions:editor.name'),
            field: 'name',
            cellStyle: {
              width: '40%',
            },
            render: (row: any) => {
              if (row.type === 'NODE') {
                return <span className={'font-weight-normal pl-5 pt-2 pb-2 non-hover'}>{row.name}</span>;
              } else if (row.type === 'GROUP') {
                return <label className={'font-weight-semi-bold mt-2 mb-0 pt-0 pb-0 non-hover'}>{row.name}</label>;
              } else {
                return (
                  <div>
                    <label
                      id={'leaf_' + row.id}
                      onClick={(event: any) => this.updateValue(event, row)}
                      className={'font-weight-normal text-dark cursor-pointer w-100 h-100 mt-0 mb-0 pt-2 pb-2 pl-5'}
                    >
                      {row.name}
                    </label>
                  </div>
                );
              }
            },
          },
          {
            title: i18n.t('transactions:editor.value'),
            cellStyle: {
              marginRight: '1%',
            },
            render: (row: any) => {
              return row.type === 'NODE' || row.type === 'GROUP' ? (
                ''
              ) : row.leafText ? (
                <div className={'form-inline cursor-pointer'} onClick={(event: any) => this.updateValue(event, row)}>
                  <div className="has-form-control-icon has-form-control-icon-right w-100 mr-4">
                    <input
                      type="text"
                      readOnly={true}
                      className={clsx('cursor-pointer', 'form-control', 'qa-text-filter', 'pr-2', 'w-100 h-100')}
                      style={{ minWidth: '90%' }}
                      title={row.leafText}
                      value={row.leafText}
                      autoComplete="off"
                    />
                  </div>
                </div>
              ) : (
                ''
              );
            },
          },
        ]}
      ></BzmTreeTable>
    );
  }
}
export default DynamicValuesPanel;
